import type {
  CapacityCircuitId,
  CapacityRead,
  CapacitySeed,
  CapacityPlantState,
  BottleneckLadderRead,
  CircuitCapacity,
  EbitdaRead,
  MetalPriceState,
  OreGradeState,
  RecoveryState,
  CapacityRecoveryProjection,
  CappedRecoveryMetal,
  RecoveryCaps,
  RevenueUplift,
  ThroughputScenario,
  WaterfallStep,
} from '@/versions/v1/schema/capacity';
import { CAPACITY_SEED, DEFAULT_CAPACITY_PLANT_STATE, CAPACITY_DESIGN_RECOVERIES } from './capacity-seed';
import { BUNKER_HILL_SMELTER_SCHEDULE, defaultSmelterPriceState } from './bunker-hill-smelter-terms';
import { shortTonsToTonnes } from './unit-conversion';
import { smelterSettlement } from './SmelterSettlementEngine';

const HOURS_PER_DAY = 24;
const PERCENT_PER_UNIT = 100;
const RECOVERY_POINT = 0.01;

/** Round a daily engine amount to cents before annualising it. */
export function annualiseDailyA(amountA: number, operatingDays: number): number {
  return Math.round(amountA * 100) / 100 * operatingDays;
}

export type CapacityInput = {
  grade: OreGradeState;
  seed?: CapacitySeed;
  prices?: MetalPriceState;
  /** The measured feed, head grades and recoveries at the caller's cursor. */
  plantState?: CapacityPlantState;
  /** Optional recovery-only override retained for pure callers and probes. */
  recoveryAnchor?: RecoveryState;
};

type CapacityScenarioResult = {
  throughputStph: number;
  recoveries: RecoveryState;
  financials: EbitdaRead;
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function throughputScale(seed: CapacitySeed, plantState: CapacityPlantState): number {
  return plantState.feedStph / seed.currentThroughputStph;
}

function referenceRecoveries(
  seed: CapacitySeed,
  recoveryAnchor?: RecoveryState,
  plantState: CapacityPlantState = DEFAULT_CAPACITY_PLANT_STATE,
): RecoveryState {
  return {
    znPct: recoveryAnchor?.znPct ?? plantState.recoveries.znPct ?? seed.zincRecoveryAtCurrentPct,
    pbPct: recoveryAnchor?.pbPct ?? plantState.recoveries.pbPct ?? seed.leadRecoveryAtCurrentPct,
    agPct: recoveryAnchor?.agPct ?? plantState.recoveries.agPct ?? plantState.recoveries.pbPct ?? seed.leadRecoveryAtCurrentPct,
  };
}

function recoveryCurveCalibration(
  seed: CapacitySeed,
  recoveryAnchor: RecoveryState,
  scale: number,
) {
  const floorOffset = seed.recoveryFloorZnPct - seed.zincRecoveryAtCurrentPct;
  const beyondFloorOffset = seed.zincRecoveryAtBeyondLimitPct - seed.recoveryFloorZnPct;
  return {
    floorStph: seed.recoveryFloorStph * scale,
    beyondLimitStph: seed.zincBeyondLimitStph * scale,
    floorZnPct: recoveryAnchor.znPct + floorOffset,
    beyondLimitZnPct: recoveryAnchor.znPct + floorOffset + beyondFloorOffset,
    beforeFloorSlope: floorOffset / ((seed.recoveryFloorStph - seed.currentThroughputStph) * scale),
    highRateSlope: Math.abs(beyondFloorOffset) /
      ((seed.zincBeyondLimitStph - seed.recoveryFloorStph) * scale),
  };
}

function rawRecoveriesAtThroughput(
  throughputStph: number,
  seed: CapacitySeed = CAPACITY_SEED,
  maintainZnFloor = false,
  grade: OreGradeState = seed.grade,
  recoveryAnchor?: RecoveryState,
  plantState: CapacityPlantState = DEFAULT_CAPACITY_PLANT_STATE,
): RecoveryState {
  const scale = throughputScale(seed, plantState);
  const current = referenceRecoveries(seed, recoveryAnchor, plantState);
  const curve = recoveryCurveCalibration(seed, current, scale);
  let zincRecoveryPct: number;

  if (throughputStph <= curve.floorStph) {
    zincRecoveryPct = current.znPct + curve.beforeFloorSlope *
      (throughputStph - plantState.feedStph);
  } else if (throughputStph <= curve.beyondLimitStph) {
    zincRecoveryPct = curve.floorZnPct - curve.highRateSlope *
      (throughputStph - curve.floorStph);
  } else {
    zincRecoveryPct = curve.beyondLimitZnPct - (throughputStph - curve.beyondLimitStph) / scale;
  }

  let leadRecoveryPct = Math.max(
    seed.leadRecoveryFloorPct,
    current.pbPct - (seed.leadRecoveryLossPerStphPct / scale) *
      (throughputStph - plantState.feedStph),
  );

  // The reference-grade curve already carries baseline high-rate recovery loss.
  // Preserve it exactly at the reference grade; off-reference grades receive a
  // separate saturation-plus-tail loss when a circuit is overloaded.
  const gradeCeilings = circuitCeilingValues(grade, seed, current, plantState);
  const referenceCeilings = circuitCeilingValues(plantState.grade, seed, current, plantState);
  const additionalOverrun = (id: CapacityCircuitId) => {
    const gradeCeiling = gradeCeilings.find((row) => row.id === id)!.ceilingStph;
    const referenceCeiling = referenceCeilings.find((row) => row.id === id)!.ceilingStph;
    return Math.max(
      0,
      Math.max(0, throughputStph - gradeCeiling) - Math.max(0, throughputStph - referenceCeiling),
    );
  };
  // D22 [syn]: phase the extra grinding penalty in with excess metal load
  // over the measured, grinding-balanced calibration. Reuse the seeded
  // half-saturation ratio; no switch at an arbitrarily small grade change.
  const excessMetalLoad = Math.max(0, grade.zn / plantState.grade.zn - 1, grade.pb / plantState.grade.pb - 1);
  const grindingWeight = excessMetalLoad / (seed.gradeOverloadHalfSaturationRatio + excessMetalLoad);
  const grindingOver = Math.max(0, throughputStph - seed.circuits.grindingCeilingStph * scale);
  const leadOver = additionalOverrun('lead-flotation');
  const zincOver = additionalOverrun('zinc-flotation');
  const overloadRecoveryLoss = (overrunStph: number, gradeCeilingStph: number): number => {
    const overloadRatio = gradeCeilingStph > 0 ? overrunStph / gradeCeilingStph : 0;
    const saturation = seed.gradeOverloadSaturationLossPct * overloadRatio /
      (seed.gradeOverloadHalfSaturationRatio + overloadRatio);
    const linearTail = Math.max(0, overloadRatio - seed.gradeOverloadLinearTailStartRatio) *
      seed.gradeOverloadLinearTailPerRatioPct;
    return saturation + linearTail;
  };
  zincRecoveryPct -= grindingWeight * overloadRecoveryLoss(grindingOver, seed.circuits.grindingCeilingStph * scale) +
    overloadRecoveryLoss(zincOver, gradeCeilings.find((row) => row.id === 'zinc-flotation')!.ceilingStph);
  leadRecoveryPct -= grindingWeight * overloadRecoveryLoss(grindingOver, seed.circuits.grindingCeilingStph * scale) +
    overloadRecoveryLoss(leadOver, gradeCeilings.find((row) => row.id === 'lead-flotation')!.ceilingStph);

  if (maintainZnFloor) zincRecoveryPct = Math.max(curve.floorZnPct, zincRecoveryPct);

  return {
    znPct: clamp(zincRecoveryPct, 0, 100),
    pbPct: clamp(leadRecoveryPct, 0, 100),
    // K11c: share Pb's curve response, anchored to Ag's own measured level.
    agPct: clamp(current.agPct + (leadRecoveryPct - current.pbPct), 0, 100),
  };
}

/**
 * D23 (second addendum): each metal's what-if cap is max(its reading design,
 * its measured recovery). K11c: Ag's own measured 83.6% sits below its 84.2%
 * design, as do Zn and Pb. The measured Current is below its own cap by
 * construction, so capping it is a no-op — nothing keys on grade equality.
 */
export function recoveryCaps(plantState: CapacityPlantState = DEFAULT_CAPACITY_PLANT_STATE): RecoveryCaps {
  const design = plantState.designRecoveries ?? CAPACITY_DESIGN_RECOVERIES;
  const measured = plantState.recoveries;
  const cap = (key: keyof RecoveryState) => measured[key] > design[key]
    ? { capPct: measured[key], basis: 'measured' as const }
    : { capPct: design[key], basis: 'design' as const };
  return { znPct: cap('znPct'), pbPct: cap('pbPct'), agPct: cap('agPct') };
}

/** D23 applies to every what-if recovery, at every grade including the measured one. */
export function capacityRecoveryProjection(
  recoveries: RecoveryState,
  plantState: CapacityPlantState = DEFAULT_CAPACITY_PLANT_STATE,
): CapacityRecoveryProjection {
  const caps = recoveryCaps(plantState);
  const cappedRecoveryMetals: CappedRecoveryMetal[] = (['znPct', 'pbPct', 'agPct'] as const)
    .filter(key => recoveries[key] > caps[key].capPct + 1e-9)
    .map(key => ({ metal: key, basis: caps[key].basis, capPct: caps[key].capPct }));
  return {
    recoveries: Object.fromEntries((['znPct', 'pbPct', 'agPct'] as const)
      .map(key => [key, Math.min(recoveries[key], caps[key].capPct)])) as RecoveryState,
    recoveryCapped: cappedRecoveryMetals.length > 0,
    cappedRecoveryMetals,
  };
}

/**
 * The measured-calibration recovery curve: the plant's own curve, never capped.
 * Consumers that are not capacity what-ifs (the advisory driver tree, uptime and
 * maintenance projections) read this so their measured calibration is unchanged.
 */
export function recoveriesAtThroughput(
  throughputStph: number,
  seed: CapacitySeed = CAPACITY_SEED,
  maintainZnFloor = false,
  grade: OreGradeState = seed.grade,
  recoveryAnchor?: RecoveryState,
  plantState: CapacityPlantState = DEFAULT_CAPACITY_PLANT_STATE,
): RecoveryState {
  return rawRecoveriesAtThroughput(throughputStph, seed, maintainZnFloor, grade, recoveryAnchor, plantState);
}

/**
 * A capacity what-if curve point: the measured curve with each metal's D23 cap
 * applied. Used by the capacity throughput curve, ladder and strategies.
 */
export function cappedRecoveriesAtThroughput(
  throughputStph: number,
  seed: CapacitySeed = CAPACITY_SEED,
  maintainZnFloor = false,
  grade: OreGradeState = seed.grade,
  recoveryAnchor?: RecoveryState,
  plantState: CapacityPlantState = DEFAULT_CAPACITY_PLANT_STATE,
): RecoveryState {
  return capacityRecoveryProjection(
    recoveriesAtThroughput(throughputStph, seed, maintainZnFloor, grade, recoveryAnchor, plantState),
    plantState,
  ).recoveries;
}

function concentrateRatesPerFeedTon(
  grade: OreGradeState,
  recoveries: RecoveryState,
  plantState: CapacityPlantState,
): { lead: number; zinc: number } {
  const grades = plantState.concentrateGrades ?? DEFAULT_CAPACITY_PLANT_STATE.concentrateGrades!;
  return {
    lead: (grade.pb * (recoveries.pbPct / PERCENT_PER_UNIT)) /
      (grades.leadPct / PERCENT_PER_UNIT),
    zinc: (grade.zn * (recoveries.znPct / PERCENT_PER_UNIT)) /
      (grades.zincPct / PERCENT_PER_UNIT),
  };
}

export function calculateEbitda(
  throughputStph: number,
  grade: OreGradeState,
  recoveries: RecoveryState,
  seed: CapacitySeed = CAPACITY_SEED,
  prices: MetalPriceState = defaultSmelterPriceState(),
  plantState: CapacityPlantState = DEFAULT_CAPACITY_PLANT_STATE,
): EbitdaRead {
  const feedShortTonsPerDay = throughputStph * HOURS_PER_DAY;
  const feedMetricTonnesPerDay = shortTonsToTonnes(feedShortTonsPerDay);
  const baselineAssays = plantState.penaltyAssays ?? DEFAULT_CAPACITY_PLANT_STATE.penaltyAssays!;
  const arsenicScale = plantState.grade.as === 0 ? 0 : grade.as / plantState.grade.as;
  const settlement = smelterSettlement({
    feedShortTonsPerDay,
    feedMetricTonnesPerDay,
    headGrades: {
      leadFraction: grade.pb,
      zincFraction: grade.zn,
      silverOzPerShortTon: grade.agOzPerShortTon,
    },
    recoveries: {
      leadPct: recoveries.pbPct,
      zincPct: recoveries.znPct,
      silverPct: recoveries.agPct,
    },
    concentrateGrades: plantState.concentrateGrades ?? DEFAULT_CAPACITY_PLANT_STATE.concentrateGrades!,
    assays: {
      leadAsPct: baselineAssays.leadAsPct * arsenicScale,
      leadHgPpm: baselineAssays.leadHgPpm,
      zincAsPct: baselineAssays.zincAsPct * arsenicScale,
      zincPbPct: baselineAssays.zincPbPct,
    },
    prices,
  });
  const operatingCostA = feedShortTonsPerDay * seed.operatingCostPerDstA;

  return {
    lead: settlement.lead,
    zinc: settlement.zinc,
    silverRevenueA: settlement.silver.netNsrA,
    totalNsrA: settlement.totalNsrA,
    treatmentChargeA: settlement.treatmentChargesA,
    transportA: settlement.transportA,
    penaltiesA: settlement.penaltiesA,
    operatingCostA,
    ebitdaA: settlement.totalNsrA - operatingCostA,
  };
}

function circuitCeilingValues(
  grade: OreGradeState,
  seed: CapacitySeed,
  recoveryAnchor?: RecoveryState,
  plantState: CapacityPlantState = DEFAULT_CAPACITY_PLANT_STATE,
): Omit<CircuitCapacity, 'utilisationPct' | 'headroomStph'>[] {
  const scale = throughputScale(seed, plantState);
  const currentRecovery = referenceRecoveries(seed, recoveryAnchor, plantState);
  const baseGrade = plantState.grade;
  const baseRates = concentrateRatesPerFeedTon(baseGrade, currentRecovery, plantState);
  const currentRates = concentrateRatesPerFeedTon(grade, currentRecovery, plantState);
  const baseTotalConcentrateRate = baseRates.lead + baseRates.zinc;
  const currentTotalConcentrateRate = currentRates.lead + currentRates.zinc;
  const referenceStph = plantState.feedStph;
  const ceilings: Omit<CircuitCapacity, 'utilisationPct' | 'headroomStph'>[] = [
    {
      id: 'crushing',
      name: 'Crushing & screening',
      ceilingStph: referenceStph / seed.circuits.crushingUtilisationAtReference,
      gradeBasis: 'Rock-volume throughput',
    },
    {
      id: 'grinding',
      name: 'Grinding',
      ceilingStph: seed.circuits.grindingCeilingStph * scale,
      gradeBasis: 'Ball-mill power and cyclone capacity',
    },
    {
      id: 'lead-flotation',
      name: 'Pb/Ag flotation',
      ceilingStph: (referenceStph / seed.circuits.leadFlotationUtilisationAtReference) *
        (baseGrade.pb / grade.pb),
      gradeBasis: 'Inverse to Pb feed grade',
    },
    {
      id: 'zinc-flotation',
      name: 'Zn flotation',
      ceilingStph: seed.circuits.zincFlotationCeilingStph * scale * (baseGrade.zn / grade.zn),
      gradeBasis: 'Inverse to Zn feed grade',
    },
    {
      id: 'dewatering',
      name: 'Dewatering',
      ceilingStph: (referenceStph / seed.circuits.dewateringUtilisationAtReference) *
        (baseTotalConcentrateRate / currentTotalConcentrateRate),
      gradeBasis: 'Total Pb and Zn concentrate rate',
    },
  ];

  return ceilings;
}

/** D18: feasible feed without changing the measured calibration basis. */
export function capacityOperatingFeed(
  requestedStph: number,
  { grade, seed = CAPACITY_SEED, recoveryAnchor, plantState = DEFAULT_CAPACITY_PLANT_STATE }: CapacityInput,
): number {
  return Math.min(requestedStph, ...circuitCeilingValues(grade, seed, recoveryAnchor, plantState).map(row => row.ceilingStph));
}

function circuitRows(
  grade: OreGradeState,
  seed: CapacitySeed,
  recoveryAnchor?: RecoveryState,
  plantState: CapacityPlantState = DEFAULT_CAPACITY_PLANT_STATE,
): CircuitCapacity[] {
  const referenceStph = capacityOperatingFeed(plantState.feedStph, { grade, seed, recoveryAnchor, plantState });
  return circuitCeilingValues(grade, seed, recoveryAnchor, plantState).map((row) => ({
    ...row,
    utilisationPct: (referenceStph / row.ceilingStph) * PERCENT_PER_UNIT,
    headroomStph: row.ceilingStph - referenceStph,
  }));
}

export function bottleneckLadder({
  grade,
  seed = CAPACITY_SEED,
  prices = defaultSmelterPriceState(),
  recoveryAnchor,
  plantState = DEFAULT_CAPACITY_PLANT_STATE,
}: CapacityInput): BottleneckLadderRead {
  const anchor = referenceRecoveries(seed, recoveryAnchor, plantState);
  const scale = throughputScale(seed, plantState);
  const recoveryFloorStph = seed.recoveryFloorStph * scale;
  const orderedCircuits = circuitCeilingValues(grade, seed, anchor, plantState).sort((a, b) => a.ceilingStph - b.ceilingStph);
  const primary = orderedCircuits[0]!;
  const currentFeed = capacityOperatingFeed(plantState.feedStph, { grade, seed, recoveryAnchor: anchor, plantState });
  const feedLimited = currentFeed < plantState.feedStph;
  const currentRecoveries = cappedRecoveriesAtThroughput(currentFeed, seed, false, grade, anchor, plantState);
  const currentFinancials = calculateEbitda(currentFeed, grade, currentRecoveries, seed, prices, plantState);
  const maxAchievableStph = Math.min(
    recoveryFloorStph,
    ...orderedCircuits.map((circuit) => circuit.ceilingStph),
  );
  const rungs: BottleneckLadderRead['rungs'][number][] = [];

  for (const circuit of orderedCircuits) {
    // Capital lifts the preceding grade bottlenecks; keep the normal curve and Zn floor.
    const recovery = capacityRecoveryProjection(rawRecoveriesAtThroughput(circuit.ceilingStph, seed, true, feedLimited ? plantState.grade : grade, anchor, plantState), plantState);
    const { recoveries } = recovery;
    const financials = calculateEbitda(circuit.ceilingStph, grade, recoveries, seed, prices, plantState);
    rungs.push({
      id: circuit.id,
      name: circuit.name,
      recoveryBasis: feedLimited ? primary.id : 'grinding',
      bottleneck: primary.id,
      recoveryCapped: recovery.recoveryCapped,
      cappedRecoveryMetals: recovery.cappedRecoveryMetals,
      ceilingStph: circuit.ceilingStph,
      headroomFromCurrentStph: circuit.ceilingStph - currentFeed,
      ebitdaA: financials.ebitdaA,
      ebitdaDeltaA: financials.ebitdaA - currentFinancials.ebitdaA,
      requiresCapital: circuit.ceilingStph > maxAchievableStph + 1e-9,
      currentBottleneck: circuit.id === primary.id,
    });
    // Keep the recovery-floor crossing rung in view, then stop the ladder.
    if (circuit.ceilingStph >= recoveryFloorStph) break;
  }

  return {
    currentThroughputStph: currentFeed,
    currentEbitdaA: currentFinancials.ebitdaA,
    recoveryFloorStph,
    rungs,
  };
}

function createScenario(
  id: ThroughputScenario['id'],
  label: string,
  throughputStph: number,
  grade: OreGradeState,
  seed: CapacitySeed,
  maintainZnFloor: boolean,
  selectable: boolean,
  recoveryAnchor: RecoveryState,
  plantState: CapacityPlantState,
  prices: MetalPriceState,
  liftGradeConstraints = false,
): ThroughputScenario {
  const recovery = capacityRecoveryProjection(rawRecoveriesAtThroughput(throughputStph, seed, maintainZnFloor, liftGradeConstraints ? plantState.grade : grade, recoveryAnchor, plantState), plantState);
  const { recoveries } = recovery;
  return {
    id,
    label,
    throughputStph,
    recoveries,
    recoveryCapped: recovery.recoveryCapped,
    cappedRecoveryMetals: recovery.cappedRecoveryMetals,
    financials: calculateEbitda(throughputStph, grade, recoveries, seed, prices, plantState),
    selectable,
  };
}

function uplift(
  from: CapacityScenarioResult,
  to: CapacityScenarioResult,
  seed: CapacitySeed,
  recoveryMaintained: boolean,
): RevenueUplift {
  return {
    fromStph: from.throughputStph,
    toStph: to.throughputStph,
    additionalStph: to.throughputStph - from.throughputStph,
    additionalDstPerDay: (to.throughputStph - from.throughputStph) * HOURS_PER_DAY,
    leadConcentrateGainDmtPerDay:
      to.financials.lead.concentrateDmtPerDay - from.financials.lead.concentrateDmtPerDay,
    zincConcentrateGainDmtPerDay:
      to.financials.zinc.concentrateDmtPerDay - from.financials.zinc.concentrateDmtPerDay,
    nsrGainA: to.financials.totalNsrA - from.financials.totalNsrA,
    ebitdaGainA: to.financials.ebitdaA - from.financials.ebitdaA,
    annualisedEbitdaGainA: annualiseDailyA(
      to.financials.ebitdaA - from.financials.ebitdaA,
      seed.daysPerYear,
    ),
    fromEbitdaA: from.financials.ebitdaA,
    toEbitdaA: to.financials.ebitdaA,
    recoveryMaintained,
  };
}

function perPptValues(
  grade: OreGradeState,
  seed: CapacitySeed,
  recoveryAnchor: RecoveryState,
  plantState: CapacityPlantState,
  prices: MetalPriceState,
): { leadSilverA: number; zincA: number } {
  const throughput = capacityOperatingFeed(plantState.feedStph, { grade, seed, recoveryAnchor, plantState });
  const base = cappedRecoveriesAtThroughput(throughput, seed, false, grade, recoveryAnchor, plantState);
  const baseNsr = calculateEbitda(throughput, grade, base, seed, prices, plantState).totalNsrA;
  const leadPlusOnePpt = {
    ...base,
    pbPct: base.pbPct + RECOVERY_POINT * PERCENT_PER_UNIT,
    agPct: base.agPct + RECOVERY_POINT * PERCENT_PER_UNIT,
  };
  const zincPlusOnePpt = { ...base, znPct: base.znPct + RECOVERY_POINT * PERCENT_PER_UNIT };

  return {
    leadSilverA: calculateEbitda(throughput, grade, leadPlusOnePpt, seed, prices, plantState).totalNsrA - baseNsr,
    zincA: calculateEbitda(throughput, grade, zincPlusOnePpt, seed, prices, plantState).totalNsrA - baseNsr,
  };
}

function recoverySeries(
  seed: CapacitySeed,
  grade: OreGradeState,
  maxAchievableStph: number,
  nextConstraintStph: number,
  targetThroughputStph: number,
  recoveryAnchor: RecoveryState,
  plantState: CapacityPlantState,
): CapacityRead['recoverySeries'] {
  const rows: CapacityRead['recoverySeries'][number][] = [];
  const currentFeed = capacityOperatingFeed(plantState.feedStph, { grade, seed, recoveryAnchor, plantState });
  const feedLimited = currentFeed < plantState.feedStph;
  const firstThroughputStph = Math.min(50, Math.floor(maxAchievableStph / 5) * 5 - (feedLimited ? 5 : 0));
  const lastThroughputStph = Math.max(75, Math.ceil(nextConstraintStph));
  const throughputPoints = new Set<number>([
    currentFeed,
    targetThroughputStph,
    maxAchievableStph,
    nextConstraintStph,
  ]);
  for (let throughputStph = firstThroughputStph; throughputStph <= lastThroughputStph; throughputStph += 1) {
    throughputPoints.add(throughputStph);
  }
  for (const throughputStph of [...throughputPoints].sort((a, b) => a - b)) {
    // At a feed-limited grade, the chart shows only feasible installed-capacity points.
    if (feedLimited && throughputStph > maxAchievableStph) continue;
    const recoveries = cappedRecoveriesAtThroughput(throughputStph, seed, false, grade, recoveryAnchor, plantState);
    rows.push({ throughputStph, znPct: recoveries.znPct, pbPct: recoveries.pbPct });
  }
  return rows;
}

function waterfallSteps(
  current: ThroughputScenario,
  maximum: ThroughputScenario,
  step2Potential: ThroughputScenario,
  step2NoCapital: ThroughputScenario,
): WaterfallStep[] {
  const movementKind = (deltaA: number): WaterfallStep['kind'] =>
    deltaA > 0 ? 'gain' : deltaA < 0 ? 'loss' : 'neutral';
  const step1DeltaA = maximum.financials.ebitdaA - current.financials.ebitdaA;
  const step2DeltaA = step2Potential.financials.ebitdaA - maximum.financials.ebitdaA;
  const recoveryCostDeltaA = step2NoCapital.financials.ebitdaA - step2Potential.financials.ebitdaA;
  return [
    {
      id: 'current',
      label: 'Current',
      kind: 'total',
      startA: 0,
      endA: current.financials.ebitdaA,
      deltaA: current.financials.ebitdaA,
    },
    {
      id: 'step1',
      label: 'Step 1',
      kind: movementKind(step1DeltaA),
      startA: current.financials.ebitdaA,
      endA: maximum.financials.ebitdaA,
      deltaA: step1DeltaA,
    },
    {
      id: 'maximum',
      label: 'Maximum',
      kind: 'total',
      startA: 0,
      endA: maximum.financials.ebitdaA,
      deltaA: maximum.financials.ebitdaA,
    },
    {
      id: 'step2-potential',
      label: 'Step 2 potential',
      kind: movementKind(step2DeltaA),
      startA: maximum.financials.ebitdaA,
      endA: step2Potential.financials.ebitdaA,
      deltaA: step2DeltaA,
    },
    {
      id: 'step2-maintained',
      label: 'With capacity',
      kind: 'total',
      startA: 0,
      endA: step2Potential.financials.ebitdaA,
      deltaA: step2Potential.financials.ebitdaA,
    },
    {
      id: 'recovery-floor-cost',
      label: 'Recovery floor cost',
      kind: movementKind(recoveryCostDeltaA),
      startA: step2Potential.financials.ebitdaA,
      endA: step2NoCapital.financials.ebitdaA,
      deltaA: recoveryCostDeltaA,
    },
    {
      id: 'step2-no-capital',
      label: 'No capital',
      kind: 'total',
      startA: 0,
      endA: step2NoCapital.financials.ebitdaA,
      deltaA: step2NoCapital.financials.ebitdaA,
    },
  ];
}

export function evaluateCapacity({
  grade,
  seed = CAPACITY_SEED,
  prices = defaultSmelterPriceState(),
  recoveryAnchor,
  plantState = DEFAULT_CAPACITY_PLANT_STATE,
}: CapacityInput): CapacityRead {
  const anchor = referenceRecoveries(seed, recoveryAnchor, plantState);
  const scale = throughputScale(seed, plantState);
  const curve = recoveryCurveCalibration(seed, anchor, scale);
  const recoveryFloorStph = curve.floorStph;
  const targetThroughputStph = seed.targetThroughputStph * scale;
  const currentFeed = capacityOperatingFeed(plantState.feedStph, { grade, seed, recoveryAnchor: anchor, plantState });
  const feedLimited = currentFeed < plantState.feedStph;
  const circuits = circuitRows(grade, seed, anchor, plantState);
  const sortedByUtilisation = [...circuits].sort((a, b) => b.utilisationPct - a.utilisationPct);
  const bottleneck = sortedByUtilisation[0]!;
  const nextConstraint = sortedByUtilisation[1]!;
  const maxAchievableStph = Math.min(
    ...circuits.map((circuit) => circuit.ceilingStph),
    recoveryFloorStph,
  );
  const current = createScenario(
    'current',
    'Current',
    currentFeed,
    grade,
    seed,
    false,
    true,
    anchor,
    plantState,
    prices,
  );
  const optimised = createScenario(
    'optimised',
    'Optimised',
    Math.min(targetThroughputStph, maxAchievableStph),
    grade,
    seed,
    true,
    true,
    anchor,
    plantState,
    prices,
  );
  const maximum = createScenario(
    'maximum',
    'Maximum achievable',
    maxAchievableStph,
    grade,
    seed,
    true,
    true,
    anchor,
    plantState,
    prices,
  );
  const beyondLimit = createScenario(
    'beyond-limit',
    feedLimited ? 'Without capital' : 'Beyond-limit',
    feedLimited ? maxAchievableStph : nextConstraint.ceilingStph,
    grade,
    seed,
    false,
    false,
    anchor,
    plantState,
    prices,
  );
  const step2PotentialScenario = createScenario(
    'beyond-limit',
    'With capacity',
    nextConstraint.ceilingStph,
    grade,
    seed,
    true,
    false,
    anchor,
    plantState,
    prices,
    feedLimited,
  );
  const step1 = uplift(current, maximum, seed, true);
  const step2Potential = uplift(maximum, step2PotentialScenario, seed, true);
  const step2WithoutCapital = uplift(maximum, beyondLimit, seed, false);
  const recoveryFloorCostA = step2Potential.ebitdaGainA - step2WithoutCapital.ebitdaGainA;
  const perPpt = perPptValues(grade, seed, anchor, plantState, prices);
  const currentAsPenaltyA = current.financials.penaltiesA;
  const annualisedRecoveryFloorCostA = annualiseDailyA(recoveryFloorCostA, seed.daysPerYear);
  const waterfall = waterfallSteps(current, maximum, step2PotentialScenario, beyondLimit).map(step =>
    feedLimited && step.id === 'recovery-floor-cost' ? {
      ...step, label: 'Capacity value',
      startA: step.endA, endA: step.startA, deltaA: -step.deltaA,
      kind: step.deltaA < 0 ? 'gain' as const : step.deltaA > 0 ? 'loss' as const : 'neutral' as const,
    } : step,
  );
  const scenarios = [current, optimised, maximum, beyondLimit] as const;
  const ladder = bottleneckLadder({ grade, seed, prices, recoveryAnchor: anchor, plantState });
  const leadAsThreshold = BUNKER_HILL_SMELTER_SCHEDULE.products.find((row) => row.product === 'Pb')!
    .penalties.find((term) => term.element === 'As')!.limit;
  const zincAsThreshold = BUNKER_HILL_SMELTER_SCHEDULE.products.find((row) => row.product === 'Zn')!
    .penalties.find((term) => term.element === 'As')!.limit;

  return {
    grade,
    prices,
    sourceTags: seed.sourceTags,
    currentThroughputStph: currentFeed,
    measuredThroughputStph: plantState.feedStph,
    targetThroughputStph,
    recoveryFloorStph,
    recoveryFloorZnPct: curve.floorZnPct,
    recoveryFloorPbPct: seed.leadRecoveryFloorPct,
    maxAchievableStph,
    bottleneck: bottleneck.id as CapacityCircuitId,
    nextConstraint: nextConstraint.id as CapacityCircuitId,
    nextConstraintStph: nextConstraint.ceilingStph,
    headroomStph: maxAchievableStph - currentFeed,
    circuits,
    bottleneckLadder: ladder,
    arsenic: {
      leadConcentratePct: current.financials.lead.arsenicAssayPct,
      zincConcentratePct: current.financials.zinc.arsenicAssayPct,
      leadThresholdPct: leadAsThreshold,
      zincThresholdPct: zincAsThreshold,
      penaltyA: currentAsPenaltyA,
    },
    perPpt,
    scenarios,
    step1,
    step2: {
      potential: step2Potential,
      withoutCapital: step2WithoutCapital,
      recoveryFloorCostA,
      annualisedRecoveryFloorCostA,
      capexA: seed.zincFlotationExpansionCapexA,
      paybackMonths: step2Potential.annualisedEbitdaGainA > 0
        ? (seed.zincFlotationExpansionCapexA / step2Potential.annualisedEbitdaGainA) * 12
        : Number.POSITIVE_INFINITY,
    },
    recoverySeries: recoverySeries(
      seed,
      grade,
      maxAchievableStph,
      nextConstraint.ceilingStph,
      targetThroughputStph,
      anchor,
      plantState,
    ),
    waterfall,
  };
}

export function capacityAtReference(
  seed: CapacitySeed = CAPACITY_SEED,
  recoveryAnchor?: RecoveryState,
  plantState: CapacityPlantState = DEFAULT_CAPACITY_PLANT_STATE,
  prices: MetalPriceState = defaultSmelterPriceState(),
): CapacityRead {
  return evaluateCapacity({ grade: plantState.grade, seed, prices, recoveryAnchor, plantState });
}
